"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseAccount = exports.formatAccount = exports.parseChain = exports.formatChain = exports.isCompatibleAddressGroup = exports.isCompatibleChain = exports.WalletConnectProvider = void 0;
const tslib_1 = require("tslib");
const eventemitter3_1 = tslib_1.__importDefault(require("eventemitter3"));
const sign_client_1 = tslib_1.__importDefault(require("@walletconnect/sign-client"));
const sign_client_2 = require("@walletconnect/sign-client");
const utils_1 = require("@walletconnect/utils");
const utils_2 = require("@walletconnect/utils");
const web3_1 = require("@alephium/web3");
const constants_1 = require("./constants");
const utils_3 = require("./utils");
const core_1 = require("@walletconnect/core");
const keyvaluestorage_1 = require("@walletconnect/keyvaluestorage");
const async_sema_1 = require("async-sema");
const REQUESTS_PER_SECOND_LIMIT = 5;
class WalletConnectProvider extends web3_1.SignerProvider {
    static async init(opts) {
        const provider = new WalletConnectProvider(opts);
        await provider.initialize();
        return provider;
    }
    constructor(opts) {
        super();
        this.events = new eventemitter3_1.default();
        this.account = undefined;
        this.rateLimit = RateLimit(REQUESTS_PER_SECOND_LIMIT);
        this.requestNodeAPI = (args) => {
            return this.typedRequest('alph_requestNodeApi', args);
        };
        this.requestExplorerAPI = (args) => {
            return this.typedRequest('alph_requestExplorerApi', args);
        };
        this.providerOpts = opts;
        this.networkId = opts.networkId;
        this.addressGroup = opts.addressGroup;
        this.permittedChain = formatChain(this.networkId, this.addressGroup);
        this.methods = opts.methods ?? [...constants_1.RELAY_METHODS];
        if (this.methods.includes('alph_requestNodeApi')) {
            this.nodeProvider = web3_1.NodeProvider.Remote(this.requestNodeAPI);
        }
        else {
            this.nodeProvider = undefined;
        }
        if (this.methods.includes('alph_requestExplorerApi')) {
            this.explorerProvider = web3_1.ExplorerProvider.Remote(this.requestExplorerAPI);
        }
        else {
            this.explorerProvider = undefined;
        }
    }
    async connect() {
        if (!this.session) {
            const { uri, approval } = await this.client.connect({
                requiredNamespaces: {
                    alephium: {
                        chains: [this.permittedChain],
                        methods: this.methods,
                        events: ['accountChanged']
                    }
                }
            });
            if (uri) {
                this.emitEvents('displayUri', uri);
            }
            this.session = await approval();
            this.updateNamespace(this.session.namespaces);
        }
        else {
            this.updateNamespace(this.session.namespaces);
        }
        await this.cleanMessages();
    }
    async disconnect() {
        if (!this.client || !this.session) {
            throw new Error('Sign Client not initialized');
        }
        await this.providerOpts.onDisconnected();
        const reason = (0, utils_2.getSdkError)('USER_DISCONNECTED');
        try {
            await this.client.disconnect({ topic: this.session.topic, reason });
        }
        catch (error) {
            await this.client.session.delete(this.session.topic, reason);
            await this.client.core.crypto.deleteSymKey(this.session.topic);
        }
        this.session = undefined;
        this.account = undefined;
    }
    on(event, listener) {
        this.events.on(event, listener);
    }
    once(event, listener) {
        this.events.once(event, listener);
    }
    removeListener(event, listener) {
        this.events.removeListener(event, listener);
    }
    off(event, listener) {
        this.events.off(event, listener);
    }
    unsafeGetSelectedAccount() {
        if (this.account === undefined) {
            throw Error('Account is not available');
        }
        return Promise.resolve(this.account);
    }
    async signAndSubmitTransferTx(params) {
        return this.typedRequest('alph_signAndSubmitTransferTx', params);
    }
    async signAndSubmitDeployContractTx(params) {
        return this.typedRequest('alph_signAndSubmitDeployContractTx', params);
    }
    async signAndSubmitExecuteScriptTx(params) {
        return this.typedRequest('alph_signAndSubmitExecuteScriptTx', params);
    }
    async signAndSubmitUnsignedTx(params) {
        return this.typedRequest('alph_signAndSubmitUnsignedTx', params);
    }
    async signAndSubmitChainedTx(params) {
        return this.typedRequest('alph_signAndSubmitChainedTx', params);
    }
    async signUnsignedTx(params) {
        return this.typedRequest('alph_signUnsignedTx', params);
    }
    async signMessage(params) {
        return this.typedRequest('alph_signMessage', params);
    }
    getCustomStoragePrefix() {
        return this.providerOpts.customStoragePrefix ?? 'alephium';
    }
    getWCStorageKey(prefix, version, name) {
        const customStoragePrefix = `:${this.getCustomStoragePrefix()}`;
        return prefix + version + customStoragePrefix + '//' + name;
    }
    async getSessionTopics(storage) {
        const sessionKey = this.getWCStorageKey(sign_client_2.SIGN_CLIENT_STORAGE_PREFIX, core_1.STORE_STORAGE_VERSION, sign_client_2.SESSION_CONTEXT);
        const sessions = await storage.getItem(sessionKey);
        if (sessions === undefined) {
            return [];
        }
        return sessions
            .filter((session) => {
            const chains = (0, utils_2.getChainsFromNamespaces)(session.namespaces, [constants_1.PROVIDER_NAMESPACE]);
            return chains.length > 0 && chains.every((c) => c.startsWith(constants_1.PROVIDER_NAMESPACE));
        })
            .map((session) => session.topic);
    }
    async cleanBeforeInit() {
        console.log('Clean storage before SignClient init');
        const storage = new keyvaluestorage_1.KeyValueStorage({ ...core_1.CORE_STORAGE_OPTIONS });
        const historyStorageKey = this.getWCStorageKey(core_1.CORE_STORAGE_PREFIX, core_1.HISTORY_STORAGE_VERSION, core_1.HISTORY_CONTEXT);
        const historyRecords = await storage.getItem(historyStorageKey);
        if (historyRecords !== undefined) {
            const remainRecords = historyRecords.filter((record) => !this.needToDeleteHistory(record));
            await storage.setItem(historyStorageKey, remainRecords);
        }
        const topics = await this.getSessionTopics(storage);
        if (topics.length > 0) {
            const messageStorageKey = this.getWCStorageKey(core_1.CORE_STORAGE_PREFIX, core_1.MESSAGES_STORAGE_VERSION, core_1.MESSAGES_CONTEXT);
            const messages = await storage.getItem(messageStorageKey);
            if (messages === undefined) {
                return;
            }
            const messagesMap = (0, utils_2.objToMap)(messages);
            topics.forEach((topic) => messagesMap.delete(topic));
            await storage.setItem(messageStorageKey, (0, utils_2.mapToObj)(messagesMap));
            console.log(`Clean topics from messages storage: ${topics.join(',')}`);
        }
    }
    needToDeleteHistory(record) {
        const request = record.request;
        if (request.method !== 'wc_sessionRequest') {
            return false;
        }
        const alphRequestMethod = request.params?.request?.method;
        return alphRequestMethod === 'alph_requestNodeApi' || alphRequestMethod === 'alph_requestExplorerApi';
    }
    cleanHistory(checkResponse) {
        try {
            const records = this.client.core.history.records;
            for (const [id, record] of records) {
                if (checkResponse && record.response === undefined) {
                    continue;
                }
                if (this.needToDeleteHistory(record)) {
                    this.client.core.history.delete(record.topic, id);
                }
            }
        }
        catch (error) {
            console.error(`Failed to clean history, error: ${error}`);
        }
    }
    async cleanMessages() {
        if (this.session !== undefined) {
            try {
                await this.client.core.relayer.messages.del(this.session.topic);
            }
            catch (error) {
                console.error(`Failed to clean messages, error: ${error}, topic: ${this.session.topic}`);
            }
        }
    }
    async initialize() {
        try {
            await this.cleanBeforeInit();
        }
        catch (error) {
            console.error(`Failed to clean storage, error: ${error}`);
        }
        await this.createClient();
        this.cleanHistory(false);
        this.checkStorage();
        this.registerEventListeners();
    }
    async createClient() {
        this.client =
            this.providerOpts.client ||
                (await sign_client_1.default.init({
                    ...this.providerOpts,
                    logger: this.providerOpts.logger || constants_1.LOGGER,
                    relayUrl: this.providerOpts.relayUrl || constants_1.RELAY_URL,
                    customStoragePrefix: this.getCustomStoragePrefix()
                }));
    }
    checkStorage() {
        const sessionKeys = this.client.session.keys;
        for (let i = sessionKeys.length - 1; i >= 0; i--) {
            const session = this.client.session.get(sessionKeys[`${i}`]);
            const hasKeyChain = this.client.core.crypto.keychain.has(session.topic);
            const chains = (0, utils_2.getChainsFromNamespaces)(session.namespaces, [constants_1.PROVIDER_NAMESPACE]);
            if (this.sameChains(chains, [this.permittedChain]) && hasKeyChain) {
                this.session = session;
                return;
            }
        }
    }
    registerEventListeners() {
        if (typeof this.client === 'undefined') {
            throw new Error('Sign Client is not initialized');
        }
        this.client.on('session_ping', (args) => {
            if (args.topic === this.session?.topic) {
                this.emitEvents('session_ping', args);
            }
        });
        this.client.on('session_event', (args) => {
            if (args.topic === this.session?.topic) {
                this.emitEvents('session_event', args);
            }
        });
        this.client.on('session_update', ({ topic, params }) => {
            if (topic === this.session?.topic) {
                const { namespaces } = params;
                const _session = this.client?.session.get(topic);
                this.session = { ..._session, namespaces };
                this.updateNamespace(this.session.namespaces);
                this.emitEvents('session_update', { topic, params });
            }
        });
        this.client.on('session_delete', (args) => {
            if (args.topic === this.session?.topic) {
                this.emitEvents('session_delete');
            }
        });
    }
    emitEvents(event, data) {
        this.events.emit(event, data);
    }
    typedRequest(method, params) {
        return this.request({ method, params });
    }
    async request(args) {
        await this.rateLimit();
        if (!this.session) {
            throw new Error('Sign Client not initialized');
        }
        if (!this.methods.includes(args.method)) {
            return Promise.reject(new Error(`Invalid method was passed: ${args.method}`));
        }
        if (!args.method.startsWith('alph_request')) {
            const signerAddress = args.params?.signerAddress;
            if (typeof signerAddress === 'undefined') {
                throw new Error('Cannot request without signerAddress');
            }
            const selectedAddress = (await this.getSelectedAccount()).address;
            if (signerAddress !== selectedAddress) {
                throw new Error(`Invalid signer address: ${args.params.signerAddress}`);
            }
        }
        try {
            const isSignRequest = args.method.startsWith('alph_sign');
            if (isSignRequest) {
                redirectToDeepLink();
            }
            const response = await this.client.request({
                request: {
                    method: args.method,
                    params: args.params
                },
                chainId: this.permittedChain,
                topic: this.session?.topic
            });
            if (!isSignRequest) {
                this.cleanHistory(true);
            }
            await this.cleanMessages();
            return response;
        }
        catch (error) {
            throw new web3_1.TraceableError(`Failed to request ${args.method}`, error);
        }
    }
    updateNamespace(namespaces) {
        const chains = (0, utils_2.getChainsFromNamespaces)(namespaces, [constants_1.PROVIDER_NAMESPACE]);
        this.setChain(chains);
        const accounts = (0, utils_2.getAccountsFromNamespaces)(namespaces, [constants_1.PROVIDER_NAMESPACE]);
        this.setAccounts(accounts);
    }
    sameChains(chains0, chains1) {
        if (typeof chains1 === 'undefined') {
            return false;
        }
        else {
            return chains0.join() === chains1.join();
        }
    }
    setChain(chains) {
        if (!this.sameChains(chains, [this.permittedChain])) {
            throw Error('Network or address group has changed');
        }
    }
    sameAccounts(account0, account1) {
        if (typeof account1 === 'undefined') {
            return false;
        }
        else {
            return account0.map((a) => a.address).join() === account1.map((a) => a.address).join();
        }
    }
    isPreauthorized() {
        if (!this.session)
            return false;
        const accounts = (0, utils_2.getAccountsFromNamespaces)(this.session.namespaces, [constants_1.PROVIDER_NAMESPACE]);
        const parsedAccounts = accounts.map(parseAccount);
        const { networkId, addressGroup, keyType } = this.providerOpts;
        return !!parsedAccounts.find((account) => networkId === account.networkId &&
            (addressGroup === undefined || account.group === addressGroup) &&
            (keyType === undefined || account.keyType === keyType));
    }
    setAccounts(accounts) {
        const parsedAccounts = accounts.map(parseAccount);
        if (this.sameAccounts(parsedAccounts, this.lastSetAccounts)) {
            return;
        }
        else {
            this.lastSetAccounts = parsedAccounts;
        }
        if (parsedAccounts.length !== 1) {
            throw Error('The WC provider does not supports multiple accounts');
        }
        const newAccount = parsedAccounts[0];
        if (!isCompatibleAddressGroup(newAccount.group, this.addressGroup)) {
            throw Error('The new account belongs to an unexpected address group');
        }
        this.account = newAccount;
        this.emitEvents('accountChanged', newAccount);
    }
}
exports.WalletConnectProvider = WalletConnectProvider;
function isCompatibleChain(chain) {
    return chain.startsWith(`${constants_1.PROVIDER_NAMESPACE}:`);
}
exports.isCompatibleChain = isCompatibleChain;
function isCompatibleAddressGroup(group, expectedAddressGroup) {
    return expectedAddressGroup === undefined || expectedAddressGroup === group;
}
exports.isCompatibleAddressGroup = isCompatibleAddressGroup;
function formatChain(networkId, addressGroup) {
    if (addressGroup !== undefined && addressGroup < 0) {
        throw Error('Address group in provider needs to be either undefined or non-negative');
    }
    const addressGroupEncoded = addressGroup !== undefined ? addressGroup : -1;
    return `${constants_1.PROVIDER_NAMESPACE}:${networkId}/${addressGroupEncoded}`;
}
exports.formatChain = formatChain;
function parseChain(chainString) {
    const [_namespace, networkId, addressGroup] = chainString.replace(/\//g, ':').split(':');
    const addressGroupDecoded = parseInt(addressGroup, 10);
    if (addressGroupDecoded < -1) {
        throw Error('Address group in protocol needs to be either -1 or non-negative');
    }
    const networkIdList = web3_1.networkIds;
    if (!networkIdList.includes(networkId)) {
        throw Error(`Invalid network id, expect one of ${networkIdList}`);
    }
    return {
        networkId: networkId,
        addressGroup: addressGroupDecoded === -1 ? undefined : addressGroupDecoded
    };
}
exports.parseChain = parseChain;
function formatAccount(permittedChain, account) {
    return `${permittedChain}:${account.publicKey}/${account.keyType}`;
}
exports.formatAccount = formatAccount;
function parseAccount(account) {
    const [_namespace, networkId, _group, publicKey, keyType] = account.replace(/\//g, ':').split(':');
    const address = (0, web3_1.addressFromPublicKey)(publicKey);
    const group = (0, web3_1.groupOfAddress)(address);
    if (keyType !== 'default' && keyType !== 'bip340-schnorr') {
        throw Error(`Invalid key type: ${keyType}`);
    }
    return { address, group, publicKey, keyType, networkId: networkId };
}
exports.parseAccount = parseAccount;
function redirectToDeepLink() {
    if ((0, utils_3.isMobile)() && (0, utils_1.isBrowser)()) {
        window.open(constants_1.ALEPHIUM_DEEP_LINK, '_self', 'noreferrer noopener');
    }
}
function RateLimit(rps) {
    const sema = new async_sema_1.Sema(rps);
    const delay = 1000;
    return async () => {
        const waitingLength = sema.nrWaiting();
        if (waitingLength > 0) {
            console.warn(`There are currently ${waitingLength} requests in the waiting queue. Please reduce the number of WalletConnect requests.`);
        }
        await sema.acquire();
        setTimeout(() => sema.release(), delay);
    };
}
//# sourceMappingURL=provider.js.map